import React, { useMemo, useState } from 'react';
import { Button } from '../../../components/ui/Button';
import { Video } from '../../../types/videos';

export type AssignVideosViewProps = {
  machineId: string;
  assigned: Video[];
  unassigned: Video[];
  loading?: boolean;
  saving?: boolean;
  error?: string | null;
  onBack: () => void;
  onAssign: (id: string) => void;
  onUnassign: (id: string) => void;
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
  onSave: () => void;
};

export const AssignVideosView: React.FC<AssignVideosViewProps> = props => {
  const [filter, setFilter] = useState('');
  const filteredUnassigned = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return props.unassigned;
    return props.unassigned.filter(v => v.filename.toLowerCase().includes(q));
  }, [filter, props.unassigned]);

  return (
    <div className="w-full">
      <div className="mb-3 flex items-center justify-between">
        <h1 className="text-xl font-semibold">Assign Videos</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={props.onBack}>Back</Button>
          <Button onClick={props.onSave} disabled={props.saving || props.loading}>{props.saving ? 'Saving...' : 'Save'}</Button>
        </div>
      </div>
      {props.error && <div className="mb-2 text-sm text-destructive">{props.error}</div>}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded border p-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="font-medium">Available</div>
            <input
              className="rounded border px-2 py-1 text-sm"
              placeholder="Search filename..."
              value={filter}
              onChange={e => setFilter(e.target.value)}
            />
          </div>
          <div className="max-h-[60vh] overflow-auto">
            {props.loading ? (
              <div className="text-sm text-muted-foreground">Loading...</div>
            ) : filteredUnassigned.length ? (
              <ul className="divide-y">
                {filteredUnassigned.map(v => (
                  <li key={v.id} className="flex items-center justify-between gap-2 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{v.filename}</div>
                      {typeof v.durationSec === 'number' && (
                        <div className="text-xs text-muted-foreground">{v.durationSec}s</div>
                      )}
                    </div>
                    <Button size="sm" onClick={() => props.onAssign(v.id)}>Add</Button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-muted-foreground">No videos.</div>
            )}
          </div>
        </div>

        <div className="rounded border p-3">
          <div className="mb-2 font-medium">Assigned (order = top to bottom)</div>
          <div className="max-h-[60vh] overflow-auto">
            {props.loading ? (
              <div className="text-sm text-muted-foreground">Loading...</div>
            ) : props.assigned.length ? (
              <ul className="divide-y">
                {props.assigned.map((v, i) => (
                  <li key={v.id} className="flex items-center justify-between gap-2 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{i + 1}. {v.filename}</div>
                      {typeof v.durationSec === 'number' && (
                        <div className="text-xs text-muted-foreground">{v.durationSec}s</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => props.onMoveUp(v.id)} disabled={i === 0}>Up</Button>
                      <Button size="sm" variant="outline" onClick={() => props.onMoveDown(v.id)} disabled={i === props.assigned.length - 1}>Down</Button>
                      <Button size="sm" variant="destructive" onClick={() => props.onUnassign(v.id)}>Remove</Button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-muted-foreground">No videos assigned.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignVideosView;
